import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    Typography,
    Grid,
    Container,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Avatar,
    Chip,
    Tooltip,
    Paper,
    TextField,
    InputAdornment,
} from "@mui/material";
import { Event, ChairAlt, CheckCircle, Cancel, Search } from "@mui/icons-material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import firebaseApp, { KEY_REF } from "../adm/admin/firebaseConfig";
import { getDatabase, ref, get } from "firebase/database";
import TitleSection from "../utils/TittleTheme";

const Sessoes = () => {
    const [sessoes, setSessoes] = useState([]);
    const [filteredSessoes, setFilteredSessoes] = useState([]);
    const [selectedSessao, setSelectedSessao] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);

    // Filtros
    const [filterNumero, setFilterNumero] = useState("");
    const [filterData, setFilterData] = useState(null);
    const [filterPauta, setFilterPauta] = useState("");

    useEffect(() => {
        const fetchSessoes = async () => {
            try {
                if (!KEY_REF) {
                    console.error("KEY_REF não definido.");
                    return;
                }

                const cacheKey = `sessoes_cache_${KEY_REF}`;
                const cacheTimestampKey = `sessoes_timestamp_${KEY_REF}`;
                const cachedData = localStorage.getItem(cacheKey);
                const cacheTimestamp = localStorage.getItem(cacheTimestampKey);
                const now = Date.now();

                if (cachedData && cacheTimestamp && now - cacheTimestamp < 30 * 60 * 1000) {
                    setSessoes(JSON.parse(cachedData));
                    return;
                }

                const databaseInstance = getDatabase(firebaseApp);
                const sessoesRef = ref(databaseInstance, `sessoes`);
                const snapshot = await get(sessoesRef);

                const data = snapshot.val();

                if (data) {
                    const sessoesArray = Object.keys(data).map((key) => ({
                        id: key,
                        ...data[key],
                    }));

                    sessoesArray.sort((a, b) => dayjs(b.data).unix() - dayjs(a.data).unix());

                    localStorage.setItem(cacheKey, JSON.stringify(sessoesArray));
                    localStorage.setItem(cacheTimestampKey, now.toString());

                    setSessoes(sessoesArray);
                } else {
                    setSessoes([]);
                }
            } catch (error) {
                console.error("Erro ao buscar as sessões:", error);
            }
        };

        fetchSessoes();
    }, [KEY_REF]);

    // Aplicar filtros sempre que os valores mudarem
    useEffect(() => {
        let result = [...sessoes];

        // Filtro por número
        if (filterNumero.trim()) {
            result = result.filter(s =>
                s.numero && s.numero.toString().toLowerCase().includes(filterNumero.toLowerCase())
            );
        }

        // Filtro por data (exata)
        if (filterData) {
            const dataFormatada = filterData.format('YYYY-MM-DD');
            result = result.filter(s => s.data === dataFormatada);
        }

        // Filtro por pauta (contém o texto)
        if (filterPauta.trim()) {
            const termo = filterPauta.toLowerCase();
            result = result.filter(s =>
                s.pautas && s.pautas.toLowerCase().includes(termo)
            );
        }

        setFilteredSessoes(result);
    }, [sessoes, filterNumero, filterData, filterPauta]);

    const handleOpenDialog = (sessao) => {
        setSelectedSessao(sessao);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedSessao(null);
    };

    const limparFiltros = () => {
        setFilterNumero("");
        setFilterData(null);
        setFilterPauta("");
    };

    return (
        <Box sx={{ marginTop: 4, pb: 6 }}>
            <TitleSection
                title="SESSÕES DA CÂMARA"
                subtitle="Registro das sessões plenárias, com pautas discutidas, observações e controle de presença dos vereadores."
            />

            {/* Filtros */}
            <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 }, mb: 4 }}>
                <Paper
                    elevation={2}
                    sx={{
                        p: 3,
                        borderRadius: 3,
                        bgcolor: 'background.paper',
                        mb: 4
                    }}
                >
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Número da sessão"
                                variant="outlined"
                                value={filterNumero}
                                onChange={(e) => setFilterNumero(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                                <DatePicker
                                    label="Data da sessão"
                                    value={filterData}
                                    onChange={(newValue) => setFilterData(newValue)}
                                    format="DD/MM/YYYY"
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            variant: "outlined",
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Buscar na pauta"
                                variant="outlined"
                                value={filterPauta}
                                onChange={(e) => setFilterPauta(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sx={{ textAlign: 'center', mt: 1 }}>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={limparFiltros}
                                sx={{ minWidth: 140 }}
                            >
                                Limpar Filtros
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>

                <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                    {filteredSessoes.map((sessao) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={sessao.id}>
                            <Card
                                elevation={2}
                                sx={{
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    transition: 'all 0.25s ease',
                                    '&:hover': {
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                        transform: 'translateY(-4px)',
                                    },
                                    bgcolor: 'background.paper',
                                }}
                            >
                                <CardActionArea onClick={() => handleOpenDialog(sessao)}>
                                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                        <Box sx={{ mb: 1.5 }}>
                                            <Avatar
                                                sx={{
                                                    bgcolor: 'primary.main',
                                                    width: 56,
                                                    height: 56,
                                                    mx: 'auto',
                                                    mb: 1,
                                                }}
                                            >
                                                <Event fontSize="large" />
                                            </Avatar>
                                            <Typography variant="h6" fontWeight={600} color="text.primary">
                                                Sessão {sessao.numero}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                {sessao.data 
                                                    ? dayjs(sessao.data).format('DD/MM/YYYY') 
                                                    : 'Data não informada'}
                                            </Typography>
                                        </Box>

                                        <Divider sx={{ my: 2 }} />

                                        <Chip
                                            icon={<ChairAlt fontSize="small" />}
                                            label={`Presenças: ${Object.values(sessao.presencas || {}).filter(p => p.presente).length} / ${Object.keys(sessao.presencas || {}).length}`}
                                            size="medium"
                                            color="primary"
                                            variant="outlined"
                                            sx={{ fontWeight: 500 }}
                                        />
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {filteredSessoes.length === 0 && (
                    <Box textAlign="center" py={8}>
                        <Typography variant="h6" color="text.secondary">
                            Nenhuma sessão encontrada com os filtros aplicados.
                        </Typography>
                    </Box>
                )}
            </Container>

            {/* Modal de Detalhes */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                fullWidth
                maxWidth="md"
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: 4,
                        overflow: 'hidden',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        py: 2.5,
                        px: 4,
                        fontWeight: 600,
                        textAlign: 'center',
                    }}
                >
                    Sessão {selectedSessao?.numero} — {selectedSessao?.data 
                        ? dayjs(selectedSessao.data).format('DD/MM/YYYY') 
                        : 'Data não informada'}
                </DialogTitle>

                <DialogContent sx={{ p: { xs: 3, md: 4 }, bgcolor: 'background.default' }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 1 }}>
                        Pautas da Sessão
                    </Typography>
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 3,
                            mb: 4,
                            borderRadius: 2,
                            whiteSpace: 'pre-wrap',
                            bgcolor: 'background.paper',
                            minHeight: 80,
                        }}
                    >
                        {selectedSessao?.pautas?.trim() || 'Nenhuma pauta registrada para esta sessão.'}
                    </Paper>

                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Observações
                    </Typography>
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 3,
                            mb: 4,
                            borderRadius: 2,
                            whiteSpace: 'pre-wrap',
                            bgcolor: 'background.paper',
                            minHeight: 80,
                        }}
                    >
                        {selectedSessao?.observacoes?.trim() || 'Nenhuma observação registrada.'}
                    </Paper>

                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Registro de Presenças
                    </Typography>

                    <Grid container spacing={2} justifyContent="center">
                        {selectedSessao &&
                            Object.entries(selectedSessao.presencas || {}).map(([id, presenca]) => (
                                <Grid item xs={6} sm={4} md={3} lg={2.4} key={id}>
                                    <Tooltip title={`${presenca.partido || 'Partido não informado'}`} arrow placement="top">
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                p: 2,
                                                borderRadius: 3,
                                                bgcolor: presenca.presente ? 'success.light' : 'error.light',
                                                border: `1px solid ${presenca.presente ? 'success.main' : 'error.main'}`,
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                                },
                                            }}
                                        >
                                            <Avatar
                                                sx={{
                                                    width: 64,
                                                    height: 64,
                                                    mb: 1.5,
                                                    bgcolor: presenca.presente ? 'success.main' : 'error.main',
                                                }}
                                            >
                                                {presenca.presente ? <CheckCircle /> : <Cancel />}
                                            </Avatar>

                                            <Typography
                                                variant="subtitle2"
                                                align="center"
                                                fontWeight={600}
                                                sx={{ lineHeight: 1.2, mb: 0.5 }}
                                            >
                                                {presenca.nome}
                                            </Typography>

                                            <Chip
                                                label={presenca.presente ? 'Presente' : 'Ausente'}
                                                size="small"
                                                color={presenca.presente ? 'success' : 'error'}
                                                variant="filled"
                                                sx={{ fontSize: '0.75rem' }}
                                            />
                                        </Box>
                                    </Tooltip>
                                </Grid>
                            ))}
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ px: 4, pb: 3, pt: 1 }}>
                    <Button
                        onClick={handleCloseDialog}
                        variant="contained"
                        color="primary"
                        sx={{ px: 5, py: 1.2, borderRadius: 2 }}
                    >
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Sessoes;